import { useState, useEffect } from 'react';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';

interface KPICardProps {
  label: string;
  value: string;
  sub?: string;
}

function KPICard({ label, value, sub }: KPICardProps) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 shadow-sm flex flex-col gap-2 min-w-0">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
      <span className="text-2xl font-bold text-slate-100">{value}</span>
      {sub && <span className="text-xs text-slate-500">{sub}</span>}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 shadow-sm animate-pulse">
      <div className="h-3 bg-slate-800 rounded w-16 mb-3" />
      <div className="h-7 bg-slate-800 rounded w-24 mb-2" />
      <div className="h-3 bg-slate-800 rounded w-20" />
    </div>
  );
}

export function KPICards() {
  const [data, setData] = useState<{
    revenue: number;
    spend: number;
    roas: number;
    calls: number;
    rpc: number;
    cpc: number;
    date: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    trpc.roas.overview.query({ tenantId: user.tenantId })
      .then(setData)
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <div className="col-span-6 bg-slate-900 border border-slate-800 rounded-xl p-5 text-center text-slate-500 text-sm">
          {error ? `Failed to load KPIs` : 'No data available for today'}
        </div>
      </div>
    );
  }

  const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
      <KPICard label="Revenue" value={`$${fmt(data.revenue)}`} sub={data.date} />
      <KPICard label="Spend" value={`$${fmt(data.spend)}`} sub={data.date} />
      <KPICard label="ROAS" value={`${data.roas.toFixed(2)}x`} />
      <KPICard label="Calls" value={data.calls.toLocaleString()} />
      <KPICard label="RPC" value={`$${data.rpc.toFixed(2)}`} />
      <KPICard label="CPC" value={`$${data.cpc.toFixed(2)}`} />
    </div>
  );
}
